<template>
  <div class="gs-app gm-settings-refresh">
    <integrations-nav v-if="$route.query.type === 'integeration'" />
    <div class="flex mb-4">
      <div class="w-full gm-settings-refresh__workspace">
        <!-- Loader -->
        <portal to="loaderTarget">
          <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
            <gd-loader :label="$t('frontend_loading')"/>
          </div>
        </portal>

        <navigation-tabs
          class="gm-nav-senders-tabs absolute px-6"
          ref="navigation-tabs"
          :nav_tabs="navTabs"
        ></navigation-tabs>

        <div class="flex flex-col w-full relative">
          <div class="mt-2">
            <div
              v-if="this.$route.query.type === 'sender' && !isLoading"
              class="px-6 pb-2 w-full flex flex-col relative"
            >
              <EmailSender @senderLinked="fetchEmailSenders(true)"
                :senders="senders"
                :userCanMail="userCanMail"
                @deleteSetting="openConfirmDeleteModal"
                @showError="showErrorForMailing()"
              />
            </div>

            <div
              v-if="this.$route.query.type === 'integeration' && !isLoading"
              class="px-6 pb-2 w-full flex flex-col relative"
            >
              <SmsSender
                :integerations="integerations"
                :userCanMail="userCanMail"
                @removeIntegeration="openConfirmDeleteModal"
                @showError="showErrorForMailing()"
              />
            </div>

            <div
              v-if="this.$route.query.type === 'recipients' && !isLoading"
              class="px-6 pb-2 w-full flex flex-col relative"
            >
              <Recipients
                :userCanMail="userCanMail"
                @showError="showErrorForMailing()"
              />
            </div>
          </div>
        </div>

        <gs-named-modal
          id="senders-detail-modal"
          name="senders-detail-modal"
        >
          <div class="p-6">
            <div class="flex relative">
              <div class="flex justify-center mb-5 w-full">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                >
                  <rect x=".5" y=".5" width="39" height="39" rx="5.5" fill="var(--brand-primary-lightest)" stroke="var(--brand-primary)"/>
                  <path d="m15 15 10 10M25 15 15 25" stroke="var(--brand-primary)" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </div>
              <div
                class="absolute right-0 cursor-pointer"
                @click="closeSenderDetailModal"
              >
                <CloseIcon class="text-grey-700 h-6 w-6"/>
              </div>
            </div>
            <VuePerfectScrollbar
              class="overflow-scroll"
              style="max-height: calc(-250px + 100vh)"
            >
              <div
                class="text-center"
                v-html="senderLinkedModulesTemplate"
              ></div>
            </VuePerfectScrollbar>
            <div class="flex text-center">
              <div
                v-if="existingVerifiedSenders && existingVerifiedSenders.length"
              >
                <p class="text-sm font-bold text-deep-dark mt-6">
                  {{ $t('frontend_groovemail_senders_deletion_have_verified_senders') }}
                </p>
                <div
                  v-if="existingVerifiedSendersType === 'email'"
                  class="flex justify-center mt-4"
                >
                  <treeselect
                    :key="1"
                    class="custom-treeselect w-1/2 sender-tree-select"
                    v-model="senderChoosenToSwitch"
                    :closeOnSelect="closeOnSelect"
                    :clearable="clearable"
                    :clearOnSelect="clearOnSelect"
                    :openDirection="openDirection"
                    :multiple="false"
                    :options="verifiedEmailSenderList"
                    :placeholder="
                      $t('frontend_groovemail_sequences_email_select_sender')
                    "
                  />
                </div>
                <div
                  v-if="existingVerifiedSendersType === 'sms'"
                  class="flex justify-center mt-4"
                >
                  <treeselect
                    :key="2"
                    class="custom-treeselect w-1/2 sender-tree-select"
                    :class="{
                      'error text-danger': this.senderSwitchInitiated && !senderChoosenToSwitch
                    }"
                    v-model="senderChoosenToSwitch"
                    :closeOnSelect="closeOnSelect"
                    :clearable="clearable"
                    :clearOnSelect="clearOnSelect"
                    :multiple="false"
                    :openDirection="openDirection"
                    :options="verifiedSmsSenderList"
                    :placeholder="
                      $t('frontend_groovemail_sequences_email_select_sender')
                    "
                  />
                </div>
                <div
                  class="text-sm text-danger"
                  v-if="this.senderSwitchInitiated && !senderChoosenToSwitch"
                >
                  {{ $t('frontend_groovemail_campaigns_create_select_sender_to_proceed') }}
                </div>
              </div>
              <div v-else>
                <p class="text-sm font-bold text-deep-dark mt-6">
                  {{ $t('frontend_groovemail_senders_deletion_do_not_have_verified_senders') }}
                </p>
              </div>
            </div>
            <div class="flex justify-center mt-6">
              <gd-button
                size="auto"
                v-if="existingVerifiedSenders && existingVerifiedSenders.length"
                @click="switchAndDeleteSender"
              >
                {{ $t('frontend_delete') }}
              </gd-button>
              <gd-button
                class="ml-2"
                variant="primary"
                size="auto"
                @click="closeSenderDetailModal"
              >
                {{ $t('frontend_close') }}</gd-button
              >
            </div>
          </div>
        </gs-named-modal>
      </div>
    </div>

    <!-- Confirmation delete modal -->
    <confirm-modal
      :ref="`confirmation-delete`"
      :name="`confirmation-delete`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_bouses_delete_bonus_text')"
      @confirm="confirmDeleteAction"
      @close="closConfirmeDeletModal"
    />
  </div>
</template>

<script>
import IntegrationsNav from "@/features/builders/IntegrationsNav.vue";
import { mapGetters } from 'vuex';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import Notify from '@groovepages/gd-ui-kit/notify';
import Treeselect from '@riophae/vue-treeselect';
import EmailSender from '@/components/senders/EmailSender.vue';
import SmsSender from '@/components/senders/SmsSender.vue';
import Recipients from '@/components/senders/Recipients.vue';
import NavigationTabs from '@/components/navigation-tabs.vue';
import { sendersUrl } from '@/utils/urls.js';
import { getData, deleteData } from '@/utils/apiHelper.js';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import demoModeValuesMixin from '@/mixin/demoModeValuesMixin.js';
import { showRequestErrorAlert } from '@/components/showDialog.js';
import themeConfig from '@/theme/themeConfig';

// import icons
import CloseIcon from '@/assets/images/icons/new/close_small_new.svg';

export default {
  name: 'Settings',
  middleware: 'auth',

  metaInfo() {
    return { title: 'User Settings' };
  },

  mixins: [demoModeValuesMixin],

  data() {
    return {
      confirmTarget: '',
      confirmTargetId: null,
      isLoading: false,
      openDirection: 'above',
      clearable: false,
      closeOnSelect: true,
      clearOnSelect: true,
      senderLinkedModulesTemplate: '',
      existingVerifiedSenders: [],
      senderChoosenToSwitch: null,
      existingVerifiedSender: null,
      existingVerifiedSendersType: '',
      senderSwitchInitiated: false,
      showSenderWithSmtpBanner: false,
      senders: [],
      integerations: [],
      appPrimaryColorDeep: themeConfig.appPrimaryColorDeep,
      appPrimaryColorExtraLight: themeConfig.appPrimaryColorExtraLight,
    };
  },

  components: {
    IntegrationsNav,
    NavigationTabs,
    CloseIcon,
    Treeselect,
    VuePerfectScrollbar,
    EmailSender,
    SmsSender,
    Recipients,
  },

  methods: {
    // getters from store.
    ...mapGetters('mailing', ['getCanMail']),


    // show loader
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    // hide loader
    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    // Used to fetch email senders
    async fetchEmailSenders(primaryLoading) {
      // show loader
      this.showLoader();

      try {
        // fetch senders data
        const sendersData = await getData(
          sendersUrl.senders,
          null,
          primaryLoading
        );
        this.senders = sendersData?.data;

        // hide loader
        this.hideLoader();
      } catch (error) {
        this.hideLoader();
        // show error message
        showRequestErrorAlert(error.response?.data?.error);
      }
    },

    // Used to fetch sms senders
    async fetchSmsSenders(primaryLoading) {
      // show loader
      this.showLoader();

      try {
        // fetch senders data
        const integerationData = await getData(
          sendersUrl.smsSenders,
          null,
          primaryLoading
        );
        this.integerations = integerationData?.data;

        // hide loader
        this.hideLoader();
      } catch (error) {
        this.hideLoader();
        // show error message
        showRequestErrorAlert(error.response?.data?.error);
      } 
    },

    // Displays error message if userCanMail is set false
    showErrorForMailing() {
      showRequestErrorAlert(this.$t('frontend_groovemail_is_now_open_in_beta_mode'));
    },

    // open delete confirm
    async openConfirmDeleteModal(target, id) {
      // set the confirm id and target
      this.confirmTarget = target;
      this.confirmTargetId = id;

      // open the modal
      this.$refs[`confirmation-delete`].open();
    },

    // close delete confirm
    closConfirmeDeletModal() {
      // reset data
      this.confirmTarget = '';
      this.confirmTargetId = null;

      // close the modal
      this.$refs[`confirmation-delete`].close();
    },

    // confirm delete action
    confirmDeleteAction() {
      if (this.confirmTarget === 'sms') {
        this.removeIntegeration();
      } else if (this.confirmTarget === 'email') {
        this.deleteSetting();
      }
    },

    // Used to delete integeration.
    async removeIntegeration() {
      // show loader
      this.showLoader();
      
      const response = await deleteData(
        `${sendersUrl.smsSenders}/${this.confirmTargetId}`
      );
      if (response?.data?.error) {
        this.senderLinkedModulesTemplate = this.sendersLinkedModules(
          response?.data?.data
        );
        if (
          response?.data?.data?.verified_senders &&
          response?.data?.data?.verified_senders?.length
        ) {
          this.existingVerifiedSenders = response.data.data.verified_senders;
        } else {
          this.existingVerifiedSenders = [];
        }
        this.senderChoosenToSwitch = null;
        this.existingVerifiedSendersType = 'sms';
        this.senderSwitchInitiated = false;
        this.existingVerifiedSender = this.confirmTargetId;
        this.$root.$emit('modal-open', 'senders-detail-modal');
      } else {
        await this.fetchSmsSenders();
      }

      // hide loader
      this.hideLoader();
    },

    // Used to delete setting.
     async deleteSetting() {
      const deleteResponse = await deleteData(`${sendersUrl.senders}/${this.confirmTargetId}`);
      if (deleteResponse?.data?.error) {
        this.senderLinkedModulesTemplate = this.sendersLinkedModules(
          deleteResponse?.data?.data
        );
        if (
          deleteResponse.data.data.verified_senders &&
          deleteResponse.data.data.verified_senders.length
        ) {
          this.existingVerifiedSenders =
            deleteResponse.data.data.verified_senders;
        } else {
          this.existingVerifiedSenders = [];
        }

        this.senderChoosenToSwitch = null;
        this.existingVerifiedSendersType = 'email';
        this.senderSwitchInitiated = false;
        this.existingVerifiedSender = this.confirmTargetId;
        this.$root.$emit('modal-open', 'senders-detail-modal');
      } else if (deleteResponse && deleteResponse.data && deleteResponse.data.message) {
        await this.fetchEmailSenders();
      }
    },

    /**
     * Used to list name of broadcast, forms, sequences where sender is used.
     * @param {object} data contains details of sender to be deleted.
     * @returns {string} returns template of sender details to be displayed.
     */
    sendersLinkedModules(data) {
      let senderDetailTemplate = `<p class="text-lg font-bold text-deep-dark mb-3"> ${this.$t(
        'frontend_groovemail_senders_deletion_swal_heading_text'
      )}</p>
        <p class="text-sm text-deep-dark mb-6"> ${this.$t(
          'frontend_groovemail_senders_deletion_swal_subheading_text'
        )}</p>`;

      if (
        data.campaigns &&
        data.campaigns.length > 0 &&
        !data.campaigns.every((element) => element === null)
      ) {
        senderDetailTemplate += `<p class="text-sm text-deep-dark font-bold mb-4">${this.$t(
          'frontend_groovemail_broadcasts_route_page_title'
        )}: <span class="font-normal">${data.campaigns.join(', ')}</span></p>`;
      }
      if (
        data.forms &&
        data.forms.length > 0 &&
        !data.forms.every((element) => element === null)
      ) {
        senderDetailTemplate += `<p class="text-sm text-deep-dark font-bold mb-4">${this.$t(
          'frontend_groovemail_forms_route_page_title'
        )}: <span class="font-normal">${data.forms.join(', ')}</span></p>`;
      }
      if (
        data.sequences &&
        data.sequences.length > 0 &&
        !data.sequences.every((element) => element === null)
      ) {
        senderDetailTemplate += `<p class="text-sm text-deep-dark font-bold">${this.$t(
          'frontend_groovemail_sequences_route_page_title'
        )}: <span class="font-normal">${data.sequences.join(', ')}</span></p>`;
      }
      return senderDetailTemplate;
    },

    // Used to close senders detail modal.
    closeSenderDetailModal() {
      this.$root.$emit('modal-close', 'senders-detail-modal');
    },

    // Used to switch & delete sender based on sender type ( email or sms).
    async switchAndDeleteSender() {
      this.senderSwitchInitiated = true;
      if (this.senderChoosenToSwitch) {
        if (this.existingVerifiedSendersType === 'email') {
          await this.switchAndDeleteAnySender('sender');
          try {
            await this.fetchEmailSenders();
            this.hideLoader();
          } catch (error) {
            this.hideLoader();
            showRequestErrorAlert(error.response?.data?.error);
          }
        } else if (this.existingVerifiedSendersType === 'sms') {
          await this.switchAndDeleteAnySender('sms-sender');
          try {
            await this.fetchSmsSenders();
            this.hideLoader();
          } catch (error) {
            this.hideLoader();
            showRequestErrorAlert(error.response?.data?.error);
          }
        }
      }
    },

    /**
     * Used to switch sender to any other verified sender & delete switched sender.
     * @param {string} url sender type ( 'sender' for email sender or 'sms-sender' for sms sender).
     */
    async switchAndDeleteAnySender(url) {
      const delPayload = { replace_sender_id: this.senderChoosenToSwitch };
      await deleteData(`${url}/${this.existingVerifiedSender}`, {
        data: delPayload,
      })
        .then((response) => {
          if (response && response.data && response.data.message) {
            this.$root.$emit('modal-close', 'senders-detail-modal');
          }
          return response;
        })
        .catch((error) => {
          return error;
        });
    },
  },

  async created() {
    try {
      // show loader
      this.showLoader();
      await this.fetchEmailSenders(true);
      if (!(this.senders && this.senders.length)) {
        this.showSenderWithSmtpBanner = true;
        if (this.showSenderWithSmtpBanner && this.userCanMail) {
          Notify.fail(
            this.$t(
              'frontend_groovemail_email_sender_please_select_smtp_driver_warning'
            )
          );
        }
      }
      await this.fetchSmsSenders(true);
      // hide loader
      this.hideLoader();
    } catch (error) {
      this.hideLoader();
      showRequestErrorAlert(error.response?.data?.error);
    }
  },

  watch: {
    // Change navigation tab or fetches senders data if there is any change in route type param.
    '$route.query.type': async function queryTypeWatcher() {
      if (this.$route.query.type === 'recipients') {
        this.$refs['navigation-tabs'].redirectToPage(this.navTabs[2], 2);
      }
      try {
        // show loader
        this.showLoader();

        if (this.$route.query.type === 'sender') {
          // fetch email senders
          this.$refs['navigation-tabs'].redirectToPage(this.navTabs[0], 0);
          await this.fetchEmailSenders(true);
        } else if (this.$route.query.type === 'integeration') {
          // fetch sms senders
          this.$refs['navigation-tabs'].redirectToPage(this.navTabs[1], 1);
          await this.fetchSmsSenders(true);
        }
        // hide loader
        this.hideLoader();
      } catch (error) {
        this.hideLoader();
        showRequestErrorAlert(error.response?.data?.error);
      }
    },
  },

  computed: {
    // Can mail/Upgraded user/Sandbox mode check
    /**
     * Returns 1 if user can mail
               0 if user can't mail.
     * @returns {string} returns whether user can mail or not.
     */
    userCanMail() {
      return this.getCanMail();
    },
    // Can mail/Upgraded user/Sandbox mode check

    /**
     * Returns navigation tabs.
     * @returns {array} returns array of navigation tabs.
     */
    navTabs() { 
     return [
        {
          tabName: 'frontend_groovemail_settings_sender_settings',
          tabRoute: 'Settings',
          tabQuery: { type: 'sender' },
        },
        {
          tabName: 'frontend_groovemail_settings_integeration_settings',
          tabRoute: 'Settings',
          tabQuery: { type: 'integeration' },
        },
        {
          tabName: 'frontend_groovemail_settings_recipients_settings',
          tabRoute: 'Settings',
          tabQuery: { type: 'recipients' },
        },
      ];
    },


    /**
     * Returns verified email senders
     * @returns {array} returns array of verified email senders.
     */
    verifiedEmailSenderList() {
      const senderList = [];
      if (this.existingVerifiedSenders && this.existingVerifiedSenders.length) {
        this.existingVerifiedSenders.forEach((element) => {
          senderList.push({ id: element.id, label: element.name });
        });
      }
      return senderList;
    },

    /**
     * Returns verified sms senders
     * @returns {array} returns array of verified sms senders.
     */
    verifiedSmsSenderList() {
      const senderList = [];
      if (this.existingVerifiedSenders && this.existingVerifiedSenders.length) {
        this.existingVerifiedSenders.forEach((element) => {
          senderList.push({ id: element.id, label: element.gateway_name });
        });
      }
      return senderList;
    },
  },
};
</script>

<style lang="scss">
.gm-nav-senders-tabs {
  top: 45px;
  z-index: 10;
}

#senders-detail-modal {
  .border-solid.border-b.border-grey-200.h-16.w-full.flex.flex-row {
    display: none;
  }
  .delete-sender-btn {
    background: #2f80ed !important;
    color: #ffffff !important;
    border-color: #2f80ed !important;
  }
}

.sender-tree-select {
  .vue-treeselect__menu {
    border: 1px solid #dcdcea !important;
  }
  .vue-treeselect__option.vue-treeselect__option--highlight:hover {
    background: #efeff5 !important;
  }
}
</style>

<style scoped>
.gm-settings-refresh {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-settings-refresh__workspace {
  min-width: 0;
  padding: 20px 24px 24px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

@media (max-width: 640px) {
  .gm-settings-refresh { padding: 18px 12px 32px; }
  .gm-settings-refresh__workspace { padding: 16px; }
}
</style>
